import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus } from 'lucide-react';
import {
  Alert,
  Button,
  DataTable,
  DataTableColumn,
  EmptyState,
  PageHeader,
  SectionHeader,
  StatusPill,
  WhatsAppContactCard,
  WhatsAppIcon,
} from '@/components/common';
import ClaimCard from '@/components/customer-dashboard/ClaimCard';
import { useCustomerClaims } from '@/hooks/useCustomerClaims';
import { WHATSAPP_NUMBER_DISPLAY, WHATSAPP_SUPPORT_URL } from '@/lib/support';
import type { ClaimCase } from '@/types/claim';
import { claimStatus } from '@/utils/claimCases';
import { formatDay } from '@/utils/productCases';

// Claims are filed through the claims assistant chat, as before.
const FILE_CLAIM_PATH = '/customer/chat';

const HISTORY_COLUMNS: DataTableColumn<ClaimCase>[] = [
  { header: 'Claim', cell: (c) => c.label, width: 'tw-w-[200px]', strong: true },
  { header: 'Product', cell: (c) => c.productTitle },
  { header: 'Plan', cell: (c) => c.planType ?? '—', width: 'tw-w-[180px]' },
  {
    header: 'Status',
    cell: (c) => {
      const { tone, label } = claimStatus(c);
      return <StatusPill tone={tone}>{label}</StatusPill>;
    },
    width: 'tw-w-[140px]',
  },
  { header: 'Closed on', cell: (c) => formatDay(c.closedAt ?? c.reviewedAt ?? c.filedAt) || '—', width: 'tw-w-[140px]' },
];

const ClaimSkeleton: React.FC = () => (
  <div aria-hidden className="tw-flex tw-animate-pulse tw-flex-col tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
    <div className="tw-flex tw-items-center tw-gap-[14px]">
      <span className="tw-h-14 tw-w-14 tw-rounded-xl tw-bg-ds-subtle" />
      <span className="tw-h-4 tw-w-1/3 tw-rounded tw-bg-ds-subtle" />
    </div>
    <span className="tw-h-1.5 tw-rounded tw-bg-ds-subtle" />
    <span className="tw-h-16 tw-rounded-xl tw-bg-ds-subtle" />
  </div>
);

/** T3 Claims (and T3E when there are none): open claims, claim history and how claims work. */
const ClaimsPage: React.FC = () => {
  const navigate = useNavigate();
  const { claims, loading, error, reload } = useCustomerClaims();
  const open = claims.filter((c) => c.stage !== 'closed');
  const history = claims.filter((c) => c.stage === 'closed');
  const empty = !loading && !error && claims.length === 0;

  const fileClaim = (
    <Button leadingIcon={Plus} onClick={() => navigate(FILE_CLAIM_PATH)}>
      File a claim
    </Button>
  );

  return (
    <div className={empty ? 'tw-mx-auto tw-flex tw-max-w-[720px] tw-flex-col tw-gap-6' : 'tw-flex tw-flex-col tw-gap-6'}>
      <PageHeader
        title="Claims"
        subtitle="File a claim and follow it until it’s resolved."
        actions={
          !empty && (
            <>
              {fileClaim}
              <Button variant="secondary" leadingIcon={WhatsAppIcon} href={WHATSAPP_SUPPORT_URL} target="_blank">
                Claim on WhatsApp
              </Button>
            </>
          )
        }
        mobileActions={
          !empty && (
            <>
              <Button size="sm" leadingIcon={Plus} onClick={() => navigate(FILE_CLAIM_PATH)}>
                File a claim
              </Button>
              <Button size="sm" variant="secondary" leadingIcon={WhatsAppIcon} href={WHATSAPP_SUPPORT_URL} target="_blank">
                WhatsApp
              </Button>
            </>
          )
        }
      />

      {error ? (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <Alert tone="danger" title="Something went wrong">
            {error}
          </Alert>
          <Button variant="secondary" className="tw-self-start" onClick={reload}>
            Try again
          </Button>
        </div>
      ) : loading ? (
        <div aria-busy className="tw-flex tw-flex-col tw-gap-4">
          <ClaimSkeleton />
        </div>
      ) : empty ? (
        <>
          <EmptyState
            icon={FileText}
            title="No claims yet"
            description="If a protected product is damaged or stops working, file a claim and track every step here."
            action={fileClaim}
          />
          <WhatsAppContactCard title="File or track a claim on WhatsApp" description="The quickest way to file, track or ask about a claim." />
        </>
      ) : (
        <>
          {open.length > 0 && (
            <section className="tw-flex tw-flex-col tw-gap-4">
              <SectionHeader title="Open claims" count={open.length} />
              <div className="tw-grid tw-gap-4">
                {open.map((claim) => (
                  <ClaimCard key={claim.id} claim={claim} />
                ))}
              </div>
            </section>
          )}

          {history.length > 0 && (
            <section className="tw-flex tw-flex-col tw-gap-4">
              <SectionHeader title="Claim history" count={history.length} />
              {/* Table from 834px; cards below, per the design system. */}
              <DataTable
                className="tw-hidden min-[834px]:tw-block"
                columns={HISTORY_COLUMNS}
                rows={history}
                rowKey={(c) => c.id}
                onRowClick={(c) => navigate(`/customer/claims/${c.id}`)}
                rowLabel={(c) => `Open claim ${c.label}`}
              />
              <div className="tw-grid tw-gap-4 min-[834px]:tw-hidden">
                {history.map((claim) => (
                  <ClaimCard key={claim.id} claim={claim} />
                ))}
              </div>
            </section>
          )}

          <Alert title="How claims are resolved">
            Approved claims are resolved by repair, replacement or store credit. You can file here or on WhatsApp at{' '}
            {WHATSAPP_NUMBER_DISPLAY}.
          </Alert>
        </>
      )}
    </div>
  );
};

export default ClaimsPage;
